import { useState, useEffect } from 'preact/hooks'
import { apiClient, authStore, InitData, User } from '../../core'
import { t } from '../../core/i18n'

interface LoginResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
  user: User
}

interface LoginWidgetProps {
  initData: InitData
}

type Mode = 'login' | 'register' | 'forgot'

/**
 * LoginWidget — mirrors HS-native auth styling.
 *
 * Centered card layout (max-width 460px):
 *   - Title ("Log ind" / "Opret konto" / "Glemt password")
 *   - Form inputs styled like ContactFormWidget (1px border, focus glow)
 *   - Filled orange submit button (--yoga-primary, white text)
 *   - Toggle link between login/register modes
 *   - "Glemt password?" link inside login mode
 *   - Logged-in success state with greeting + log-out
 *   - Inline red error text on failures
 *   - Loading state: button disabled with spinner-text
 *
 * All existing auth logic preserved:
 *   - apiClient.post('/api/widget/auth/login' | '/auth/register')
 *   - authStore.setTokens / setUser / clearTokens
 *   - authStore.subscribe lifecycle
 *   - Custom events: holdstyring-widget-login / -register / -logout
 *
 * Forgot-password mode currently displays a "contact tenant" message — kept
 * as-is since no reset endpoint exists. Restyled to match the card.
 */
export function LoginWidget({ initData }: LoginWidgetProps) {
  const str = t(initData.locale)
  const [mode, setMode] = useState<Mode>('login')
  const [user, setUser] = useState<User | null>(authStore.getUser())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forgotSent, setForgotSent] = useState(false)
  // Konto uden adgangskode (fx importerede medlemmer): serveren har gemt den
  // valgte kode som afventende og sendt en bekræftelsesmail hertil.
  const [setupSentTo, setSetupSentTo] = useState<string | null>(null)
  // F-T14-3: serverens forslag til en rettet adresse, når domænet ikke kan modtage mail
  // (EMAIL_DOMAIN_NO_MAIL). Vises under e-mailfeltet som på vores egen opret-side.
  const [emailForslag, setEmailForslag] = useState<string | null>(null)

  // Form fields
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')

  // Subscribe to auth changes (multi-widget sync)
  useEffect(() => {
    return authStore.subscribe(setUser)
  }, [])

  // Clear errors and password when switching mode so prior state doesn't bleed
  // across screens. Email/firstName/lastName/phone persist for UX (toggling
  // login <-> register shouldn't force re-typing identity), but passwords must
  // never linger across mode changes.
  useEffect(() => {
    setError(null)
    setPassword('')
    setForgotSent(false)
    setSetupSentTo(null)
    setEmailForslag(null)
  }, [mode])

  async function handleLogin(e: Event) {
    e.preventDefault()
    if (loading) return
    setLoading(true)
    setError(null)

    const result = await apiClient.post<LoginResponse>('/api/widget/auth/login', {
      email,
      password,
    })

    setLoading(false)

    if (result.code === 'PASSWORD_SETUP_REQUIRED') {
      setSetupSentTo(email)
      setPassword('')
      return
    }
    if (result.code === 'PASSWORD_TOO_SHORT') {
      setError(str.passwordTooShort)
      return
    }
    if (result.error) {
      setError(result.error)
      return
    }

    if (result.data) {
      authStore.setTokens(result.data.accessToken, result.data.refreshToken)
      authStore.setUser(result.data.user)
      setPassword('')
      setSetupSentTo(null)

      window.dispatchEvent(new CustomEvent('holdstyring-widget-login', {
        detail: { user: result.data.user }
      }))
    }
  }

  async function handleRegister(e: Event) {
    e.preventDefault()
    if (loading) return
    setLoading(true)
    setError(null)

    const result = await apiClient.post<LoginResponse>('/api/widget/auth/register', {
      email,
      password,
      firstName,
      lastName,
      phone: phone || undefined,
    })

    setLoading(false)

    if (result.error) {
      setError(result.error)
      setEmailForslag(result.code === 'EMAIL_DOMAIN_NO_MAIL' && result.suggestion ? result.suggestion : null)
      return
    }
    setEmailForslag(null)

    if (result.data) {
      authStore.setTokens(result.data.accessToken, result.data.refreshToken)
      authStore.setUser(result.data.user)
      setPassword('')

      window.dispatchEvent(new CustomEvent('holdstyring-widget-register', {
        detail: { user: result.data.user }
      }))
    }
  }

  async function handleForgot(e: Event) {
    e.preventDefault()
    if (loading) return
    setLoading(true)
    setError(null)

    // Serveren svarer altid success (ingen email-enumeration); vi viser samme
    // bekræftelse uanset om kontoen findes. Kun ægte fejl (rate-limit, netværk)
    // vises som fejl.
    const result = await apiClient.post('/api/widget/auth/forgot-password', { email })

    setLoading(false)

    if (result.error) {
      setError(result.error)
      return
    }
    setForgotSent(true)
  }

  function handleLogout() {
    authStore.clearTokens()
    setEmail('')
    setPassword('')
    setFirstName('')
    setLastName('')
    setPhone('')
    setMode('login')

    window.dispatchEvent(new CustomEvent('holdstyring-widget-logout'))
  }

  // === Logged-in state ===
  if (user) {
    const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email
    return (
      <div>
        <style>{loginStyles}</style>
        <div class="hsw-login-card hsw-login-success">
          <div class="hsw-login-avatar" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h3 class="hsw-login-title">{str.welcomeName.replace('{name}', displayName)}</h3>
          <p class="hsw-login-subtitle">{user.email}</p>
          <button type="button" class="hsw-login-link hsw-login-logout" onClick={handleLogout}>
            {str.logout}
          </button>
        </div>
      </div>
    )
  }

  // === Forgot password ===
  if (mode === 'forgot') {
    return (
      <div>
        <style>{loginStyles}</style>
        <div class="hsw-login-card">
          <h3 class="hsw-login-title">{str.forgotPassword}</h3>
          {forgotSent ? (
            <>
              <p class="hsw-login-subtitle">{str.forgotPasswordSent}</p>
              <button type="button" class="hsw-login-submit" onClick={() => setMode('login')}>
                {str.backToLogin}
              </button>
            </>
          ) : (
            <>
              <p class="hsw-login-subtitle">{str.forgotPasswordInstruction}</p>
              <form class="hsw-login-form" onSubmit={handleForgot} noValidate>
                <div class="hsw-login-field">
                  <label class="hsw-login-label" for="hsw-login-email-forgot">{str.fieldEmail}</label>
                  <input
                    id="hsw-login-email-forgot"
                    class={`hsw-login-input${error ? ' hsw-login-input-error' : ''}`}
                    type="email"
                    value={email}
                    onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                    required
                    autocomplete="email"
                    placeholder={str.emailPlaceholder}
                    disabled={loading}
                  />
                </div>
                {error && <div class="hsw-login-error" role="alert">{error}</div>}
                <button type="submit" class="hsw-login-submit" disabled={loading}>
                  {loading ? str.sending : str.forgotPasswordSubmit}
                </button>
              </form>
              <button type="button" class="hsw-login-link" onClick={() => setMode('login')}>
                {str.backToLogin}
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  // === Register form ===
  if (mode === 'register') {
    return (
      <div>
        <style>{loginStyles}</style>
        <div class="hsw-login-card">
          <h3 class="hsw-login-title">{str.createAccount}</h3>
          <p class="hsw-login-subtitle">{str.createAccountAt.replace('{tenant}', initData.tenantName)}</p>

          <form class="hsw-login-form" onSubmit={handleRegister} noValidate>
            <div class="hsw-login-row">
              <div class="hsw-login-field">
                <label class="hsw-login-label" for="hsw-login-firstname">{str.fieldFirstName}</label>
                <input
                  id="hsw-login-firstname"
                  class="hsw-login-input"
                  type="text"
                  value={firstName}
                  onInput={(e) => setFirstName((e.target as HTMLInputElement).value)}
                  required
                  autocomplete="given-name"
                  disabled={loading}
                />
              </div>
              <div class="hsw-login-field">
                <label class="hsw-login-label" for="hsw-login-lastname">{str.fieldLastName}</label>
                <input
                  id="hsw-login-lastname"
                  class="hsw-login-input"
                  type="text"
                  value={lastName}
                  onInput={(e) => setLastName((e.target as HTMLInputElement).value)}
                  required
                  autocomplete="family-name"
                  disabled={loading}
                />
              </div>
            </div>

            <div class="hsw-login-field">
              <label class="hsw-login-label" for="hsw-login-email-reg">{str.fieldEmail}</label>
              <input
                id="hsw-login-email-reg"
                class={`hsw-login-input${error ? ' hsw-login-input-error' : ''}`}
                type="email"
                value={email}
                onInput={(e) => { setEmail((e.target as HTMLInputElement).value); setEmailForslag(null) }}
                required
                autocomplete="email"
                placeholder={str.emailPlaceholder}
                disabled={loading}
              />
              {emailForslag && (
                <p class="hsw-login-suggestion" role="status">
                  {str.emailDidYouMean}{' '}
                  <button
                    type="button"
                    class="hsw-login-link"
                    onClick={() => { setEmail(emailForslag); setEmailForslag(null); setError(null) }}
                  >
                    {emailForslag}
                  </button>?
                </p>
              )}
            </div>

            <div class="hsw-login-field">
              <label class="hsw-login-label" for="hsw-login-phone">
                {str.fieldPhone} <span class="hsw-login-optional">{str.optionalSuffix}</span>
              </label>
              <input
                id="hsw-login-phone"
                class="hsw-login-input"
                type="tel"
                value={phone}
                onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
                autocomplete="tel"
                disabled={loading}
              />
            </div>

            <div class="hsw-login-field">
              <label class="hsw-login-label" for="hsw-login-password-reg">{str.fieldPasswordMin}</label>
              <input
                id="hsw-login-password-reg"
                class={`hsw-login-input${error ? ' hsw-login-input-error' : ''}`}
                type="password"
                value={password}
                onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                required
                minLength={8}
                autocomplete="new-password"
                disabled={loading}
              />
            </div>

            {error && (
              <div class="hsw-login-error" role="alert">{error}</div>
            )}

            <button
              type="submit"
              class="hsw-login-submit"
              disabled={loading}
            >
              {loading ? str.creatingAccount : str.createAccount}
            </button>
          </form>

          <p class="hsw-login-toggle">
            {str.alreadyHaveAccount}{' '}
            <button
              type="button"
              class="hsw-login-link"
              onClick={() => setMode('login')}
            >
              {str.login}
            </button>
          </p>
        </div>
      </div>
    )
  }

  // === Login form (default) ===
  return (
    <div>
      <style>{loginStyles}</style>
      <div class="hsw-login-card">
        <h3 class="hsw-login-title">{str.login}</h3>
        <p class="hsw-login-subtitle">{str.loginAt.replace('{tenant}', initData.tenantName)}</p>

        <form class="hsw-login-form" onSubmit={handleLogin} noValidate>
          <div class="hsw-login-field">
            <label class="hsw-login-label" for="hsw-login-email">{str.fieldEmail}</label>
            <input
              id="hsw-login-email"
              class={`hsw-login-input${error ? ' hsw-login-input-error' : ''}`}
              type="email"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              required
              autocomplete="email"
              placeholder={str.emailPlaceholder}
              disabled={loading}
            />
          </div>

          <div class="hsw-login-field">
            <label class="hsw-login-label" for="hsw-login-password">{str.fieldPassword}</label>
            <input
              id="hsw-login-password"
              class={`hsw-login-input${error ? ' hsw-login-input-error' : ''}`}
              type="password"
              value={password}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              required
              autocomplete="current-password"
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          {setupSentTo && (
            <div class="hsw-login-info" role="status">
              <strong>{str.passwordSetupTitle}</strong>
              <p>{str.passwordSetupBody.replace('{email}', setupSentTo)}</p>
            </div>
          )}

          {error && (
            <div class="hsw-login-error" role="alert">{error}</div>
          )}

          <button
            type="submit"
            class="hsw-login-submit"
            disabled={loading}
          >
            {loading ? str.loggingIn : str.login}
          </button>
        </form>

        <div class="hsw-login-meta">
          <button
            type="button"
            class="hsw-login-link"
            onClick={() => setMode('forgot')}
          >
            {str.forgotPasswordQ}
          </button>
        </div>

        <p class="hsw-login-toggle">
          {str.noAccountQ}{' '}
          <button
            type="button"
            class="hsw-login-link"
            onClick={() => setMode('register')}
          >
            {str.createOne}
          </button>
        </p>
      </div>
    </div>
  )
}

const loginStyles = `
  .hsw-login-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 2.5rem 2rem;
    max-width: 460px;
    margin: 0 auto;
  }
  .hsw-login-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    margin: 0 0 0.4rem;
    letter-spacing: -0.01em;
  }
  .hsw-login-subtitle {
    font-size: 0.95rem;
    color: var(--yoga-text-muted, #737373);
    margin: 0 0 1.75rem;
    line-height: 1.5;
  }
  .hsw-login-form {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
  }
  .hsw-login-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.1rem;
  }
  @media (max-width: 480px) {
    .hsw-login-row { grid-template-columns: 1fr; }
    .hsw-login-card { padding: 2rem 1.25rem; }
  }
  .hsw-login-field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .hsw-login-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: 0.01em;
  }
  .hsw-login-optional {
    font-weight: 400;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-login-input {
    width: 100%;
    box-sizing: border-box;
    background: var(--yoga-card-bg, #ffffff);
    color: var(--yoga-text, #0a0a0a);
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 0);
    padding: 0.85rem 1rem;
    font-size: 0.95rem;
    font-family: inherit;
    line-height: 1.4;
    transition: border-color .2s, box-shadow .2s;
    outline: none;
  }
  .hsw-login-input::placeholder {
    color: var(--yoga-text-muted, #a3a3a3);
  }
  .hsw-login-input:hover:not(:disabled) {
    border-color: rgba(0,0,0,0.25);
  }
  .hsw-login-input:focus {
    border-color: var(--yoga-primary, #FF6B35);
    box-shadow: 0 0 0 3px rgba(255,107,53,0.15);
  }
  .hsw-login-input:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .hsw-login-input-error {
    border-color: #dc2626;
  }
  .hsw-login-input-error:focus {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220,38,38,0.15);
  }
  .hsw-login-error {
    margin-top: -0.25rem;
    color: #dc2626;
    background: rgba(220,38,38,0.07);
    border: 1px solid rgba(220,38,38,0.25);
    border-radius: var(--yoga-radius, 0);
    padding: 0.6rem 0.85rem;
    font-size: 0.875rem;
    line-height: 1.4;
  }
  .hsw-login-suggestion {
    margin: 0.4rem 0 0;
    color: #b45309;
    font-size: 0.875rem;
    line-height: 1.4;
  }
  .hsw-login-suggestion .hsw-login-link {
    font-weight: 600;
  }
  .hsw-login-info {
    margin-top: -0.25rem;
    color: var(--yoga-text, #0a0a0a);
    background: rgba(37,99,235,0.07);
    border: 1px solid rgba(37,99,235,0.25);
    border-radius: var(--yoga-radius, 0);
    padding: 0.75rem 0.9rem;
    font-size: 0.875rem;
    line-height: 1.45;
  }
  .hsw-login-info p {
    margin: 0.3rem 0 0;
  }
  .hsw-login-submit {
    margin-top: 0.25rem;
    background: var(--yoga-primary, #FF6B35);
    color: #ffffff;
    border: 1px solid var(--yoga-primary, #FF6B35);
    border-radius: var(--yoga-radius, 0);
    padding: 0.9rem 1.25rem;
    font-size: 0.95rem;
    font-weight: 600;
    font-family: inherit;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background .2s, border-color .2s, transform .1s;
    width: 100%;
  }
  .hsw-login-submit:hover:not(:disabled) {
    background: var(--yoga-primary-900, #1A1A2E);
    border-color: var(--yoga-primary-900, #1A1A2E);
  }
  .hsw-login-submit:active:not(:disabled) {
    transform: translateY(1px);
  }
  .hsw-login-submit:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .hsw-login-meta {
    margin-top: 0.85rem;
    text-align: right;
  }
  .hsw-login-toggle {
    margin: 1.5rem 0 0;
    text-align: center;
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-login-link {
    background: none;
    border: 0;
    padding: 0;
    margin: 0;
    color: var(--yoga-primary, #FF6B35);
    font-size: inherit;
    font-family: inherit;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color .2s, color .2s;
  }
  .hsw-login-link:hover {
    border-bottom-color: var(--yoga-primary, #FF6B35);
  }
  .hsw-login-link:focus-visible {
    outline: 2px solid var(--yoga-primary, #FF6B35);
    outline-offset: 2px;
    border-radius: 2px;
  }
  .hsw-login-success {
    text-align: center;
    padding: 3rem 2rem;
  }
  .hsw-login-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 999px;
    background: rgba(255,107,53,0.12);
    color: var(--yoga-primary, #FF6B35);
    margin: 0 auto 1.25rem;
  }
  .hsw-login-success .hsw-login-title {
    margin-bottom: 0.4rem;
  }
  .hsw-login-success .hsw-login-subtitle {
    margin-bottom: 1.5rem;
  }
  .hsw-login-logout {
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-login-logout:hover {
    color: var(--yoga-text, #0a0a0a);
    border-bottom-color: var(--yoga-text, #0a0a0a);
  }
`
